import { CellContext } from '@tanstack/react-table';
import {
  UserRoundMinus,
  UserRoundCheck,
  LockKeyhole,
  Pencil,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { MembershipDto } from '@area-restrita/api-client/model/membershipDto';
import {
  useUpdateMembership,
  useChangeMemberPassword,
} from '@area-restrita/api-client/endpoints/memberships/memberships.gen';

import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import Dialog from '@/components/template/Dialog';
import ChangePasswordDialog from '@/components/dialogs/ChangePasswordDialog';
import EditMemberDialog from '@/modules/tenant/memberships/components/EditMemberDialog';
import type { ChangePasswordFormData } from '@/components/dialogs/ChangePasswordDialog/schema';

type IProps = CellContext<MembershipDto, unknown>;

const UsersActionsCell: React.FC<IProps> = ({ row }) => {
  const isActive = row.original.status !== 'INACTIVE';

  const { checkAuthorization } = useAuthSessionStore();

  const canUpdate = checkAuthorization(({ can }) =>
    can('update', 'memberships'),
  );

  const canChangePassword = checkAuthorization(({ can }) =>
    can('changePassword', 'memberships'),
  );

  const { mutateAsync: updateMembership } = useUpdateMembership();
  const { mutateAsync: changePassword } = useChangeMemberPassword();

  const handleChangePassword = async (data: ChangePasswordFormData) => {
    try {
      await changePassword({
        userId: row.original.user.id,
        data: { newPassword: data.newPassword },
      });
      toast.success('Senha alterada com sucesso!');
    } catch (error) {
      toast.error('Falha ao alterar a senha. Tente novamente.');
      throw error;
    }
  };

  return (
    <div className="flex justify-end gap-2">
      <EditMemberDialog membership={row.original}>
        <Button variant="outline" size="icon-sm" disabled={!canUpdate}>
          <Pencil className="h-4 w-4" />
        </Button>
      </EditMemberDialog>

      {isActive && (
        <Dialog
          title="Inativar Membro"
          tooltipContent="Impedir membro de acessar a plataforma"
          trigger={
            <Button variant="destructive" size="icon-sm" disabled={!canUpdate}>
              <UserRoundMinus className="h-4 w-4" />
            </Button>
          }
        >
          {({ handleClose }) => (
            <>
              <p>Tem certeza que deseja inativar este membro?</p>
              <DialogFooter>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() =>
                    updateMembership(
                      {
                        userId: row.original.user.id,
                        data: { status: 'INACTIVE' },
                      },
                      {
                        onSuccess: () =>
                          toast.success('Membro inativado com sucesso!'),
                        onError: () => toast.error('Erro ao inativar membro!'),
                        onSettled: () => handleClose(),
                      },
                    )
                  }
                >
                  Inativar
                </Button>
              </DialogFooter>
            </>
          )}
        </Dialog>
      )}
      {!isActive && (
        <Dialog
          title="Ativar Membro"
          tooltipContent="Habilitar membro para acessar a plataforma"
          trigger={
            <Button variant="outline" size="icon-sm" disabled={!canUpdate}>
              <UserRoundCheck className="h-4 w-4" />
            </Button>
          }
        >
          {({ handleClose }) => (
            <>
              <p>Tem certeza que deseja ativar este membro?</p>
              <DialogFooter>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() =>
                    updateMembership(
                      {
                        userId: row.original.user.id,
                        data: { status: 'ACTIVE' },
                      },
                      {
                        onSuccess: () =>
                          toast.success('Membro ativado com sucesso!'),
                        onError: () => toast.error('Erro ao ativar membro!'),
                        onSettled: () => handleClose(),
                      },
                    )
                  }
                >
                  Ativar
                </Button>
              </DialogFooter>
            </>
          )}
        </Dialog>
      )}

      {canChangePassword && (
        <Dialog
          title="Trocar senha"
          tooltipContent="Forçar nova senha"
          trigger={
            <Button variant="outline" size="icon-sm">
              <LockKeyhole className="h-4 w-4" />
            </Button>
          }
        >
          {({ handleClose }) => (
            <ChangePasswordDialog
              userName={row.original.user.fullName ?? row.original.user.email}
              onSubmit={async (data) => {
                await handleChangePassword(data);
                handleClose();
              }}
              handleClose={handleClose}
            />
          )}
        </Dialog>
      )}
    </div>
  );
};

export default UsersActionsCell;
